import { DocumentIcon, FilmIcon, PhotographIcon } from '@heroicons/react/solid';
import { Box, CircularProgress, Stack, Typography } from '@mui/joy';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useCategories from '../../hooks/useCategories';
import { useLibraryFiles } from '../../hooks/useLibraryFiles';
import { useUser } from '../../hooks/useUser';
import { UploadForm } from './LibraryUploadForm';
import { CreateFileModalButton } from './createFileModalButton';
import { LibraryFileDetailsModal } from './fileDetailsModal';
import { deleteFileById } from '../../redux/services/libraryServices';
import {
  isImageFile,
  isPdfFile,
  isVideoFile,
} from '../../shared/files/fileType';

type RowIconProps = {
  fileUrl: string;
  size: Number;
};

const RowIcon = (props: RowIconProps) => {
  const { fileUrl, size } = props;
  if (!fileUrl) return null;
  const style = `h-${size} mt-3 text-aqua`;

  // Shared, case-insensitive classification — the old switch compared raw
  // extensions, so `.JPEG`/`.PNG` uploads fell through to the document icon
  // and logged a console error on every render of the row.
  if (isImageFile(fileUrl)) return <PhotographIcon className={`${style} `} />;
  if (isVideoFile(fileUrl)) return <FilmIcon className={`${style} `} />;
  if (isPdfFile(fileUrl)) return <DocumentIcon className={`${style} `} />;
  return <DocumentIcon className={style} />;
};

const LibraryRow = (props: any) => {
  const { filename, modifiedOn, category, fileUrl, onClick } = props;

  return (
    <ul>
      <button
        onClick={onClick}
        type="button"
        className="grid grid-cols-12 font-bold text-grey-medium rounded-mdhover:shadow-md lg:py-3 border-b-2 w-full text-left py-1"
      >
        <li className="col-span-5 flex space-x-3 font-normal">
          <RowIcon fileUrl={fileUrl} size={8} />
          <p className="mt-1">{filename}</p>
        </li>
        <li className="hidden lg:block col-span-3 font-normal h-full align-middle">
          {modifiedOn}
        </li>
        <li className="hidden lg:block col-span-3 font-normal">{category}</li>
      </button>
    </ul>
  );
};

LibraryRow.propTypes = {
  filename: PropTypes.string.isRequired,
  fileUrl: PropTypes.string.isRequired,
  modifiedOn: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

type Props = {};

export const LibraryFileList = (props: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const { t } = useTranslation();
  const categoryQuery = useCategories();
  const userQuery = useUser();
  const libraryFilesQuery = useLibraryFiles();

  useEffect(() => {
    // When file details modal closes, reset selected file
    if (!fileModalOpen) {
      setSelectedFileId(null);
    }
  }, [fileModalOpen]);

  const files = useMemo(() => {
    if (!libraryFilesQuery.isSuccess) {
      return [];
    }

    const libraryFiles = libraryFilesQuery.data;
    return libraryFiles;
  }, [libraryFilesQuery.data]);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'action',
        headerName: '',
        flex: 0.3,

        renderCell: (params) => {
          return <RowIcon fileUrl={params.row.fileUrl} size={6} />;
        },
      },
      { field: 'title', headerName: 'Bestandnaam', flex: 1.5 },
      {
        field: 'createdAt',
        headerName: 'Toegevoegd op',
        flex: 1,
        valueGetter: (createdAt) => {
          return dayjs(createdAt).format('DD/MM/YYYY');
        },
        t,
      },
      {
        field: 'category',
        headerName: 'Categorie',
        flex: 1,
      },
    ];
  }, []);

  if (!categoryQuery.isSuccess) {
    return <CircularProgress />;
  }

  const isAdmin =
    userQuery.data?.role === 'ADMIN' || userQuery.data?.role === 'SUPER_ADMIN';

  return (
    <>
      {showModal && (
        <UploadForm
          setOpen={(value) => {
            setShowModal(value);
          }}
          show={showModal}
        />
      )}

      {selectedFileId && (
        <LibraryFileDetailsModal
          toggleModal={setFileModalOpen}
          show={fileModalOpen}
          fileId={selectedFileId}
          deleteFile={async (id: string) => {
            await deleteFileById(id);
            await libraryFilesQuery.refetch();
            setFileModalOpen(false);
          }}
        />
      )}

      <Stack spacing={3}>
        <Typography level="h1">{t('navbar.navbar_library')}</Typography>

        <DataGrid
          autoHeight
          rows={files}
          columns={columns}
          getRowId={(row) => row._id}
          onRowClick={(params) => {
            setSelectedFileId(params.row._id);
            setFileModalOpen(true);
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer', // Make cursor a pointer on row hover
            },
          }}
        />

        {isAdmin && (
          <Box sx={{ display: 'flex', justifyContent: 'end' }}>
            <CreateFileModalButton
              onClick={() => {
                setShowModal(true);
              }}
            />
          </Box>
        )}
      </Stack>
    </>
  );
};
